import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { TransformedGuideData, GuideProgress, ViewModeType } from '@/types';
import { GuideStep, StepTranslation, GuideLanguage } from '@/services/apis/guides';
import { useGuideProgress, useUpdateGuideProgress } from '@/hooks/useGuides';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import GuideViewerHeader from './GuideViewerHeader';
import GuideViewerSteps from './GuideViewerSteps';
import GuideSlideView from './GuideSlideView';
import GuideScrollView from './GuideScrollView';
import GuideViewerGlossaries from './GuideViewerGlossaries';
import GuideStepAudioPlayer from './GuideStepAudioPlayer';

interface GuideViewerProps {
  guideData: TransformedGuideData;
  guideId: string;
  isPreviewMode?: boolean;
}

const GuideViewer: React.FC<GuideViewerProps> = ({ guideData, guideId, isPreviewMode = false }) => {
  const navigate = useNavigate();
  const { currentLanguage, setLanguage } = useLanguage();

  const [viewMode, setViewMode] = useState<ViewModeType>(ViewModeType.SLIDE);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const [hasNavigatedToSavedStep, setHasNavigatedToSavedStep] = useState(false);

  // Use React Query for guide progress
  const { data: progressResponse } = useGuideProgress(guideId, currentLanguage as GuideLanguage, !isPreviewMode);

  const { mutate: updateProgress, isPending: isSavingComplete } = useUpdateGuideProgress(
    guideId,
    currentLanguage as GuideLanguage,
  );

  // Extract progress from response or use default
  const progress: GuideProgress | null = useMemo(() => {
    if (isPreviewMode) return null;
    if (progressResponse?.data) {
      return progressResponse.data as GuideProgress;
    }
    // Return default progress if no data
    return {
      id: '',
      guideId,
      currentStepId: null,
      completedStepIds: [],
      quizScores: {},
      isCompleted: false,
      completedAt: null,
      startTime: null,
      language: currentLanguage || 'en',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, [progressResponse, isPreviewMode, guideId, currentLanguage]);

  // Debounced save for step navigation (auto-save current step)
  const debouncedSaveCurrentStep = useDebouncedCallback((currentStepId: string) => {
    if (!progress || isPreviewMode) return;
    updateProgress({
      currentStepId,
      completedStepIds: progress.completedStepIds,
      quizScores: progress.quizScores,
    });
  }, 500);

  // Reset navigation flag when guide or language changes
  useEffect(() => {
    setHasNavigatedToSavedStep(false);
  }, [guideId, currentLanguage]);

  // Navigate to saved current step when progress is loaded
  useEffect(() => {
    if (progress?.currentStepId && !hasNavigatedToSavedStep && !isPreviewMode && guideData.steps.length > 0) {
      const savedStepIndex = guideData.steps.findIndex((step) => step.id === progress.currentStepId);
      if (savedStepIndex !== -1) {
        setCurrentStepIndex(savedStepIndex);
        setHasNavigatedToSavedStep(true);
      }
    }
  }, [progress, hasNavigatedToSavedStep, isPreviewMode, guideData.steps]);

  // Update progress when current step changes
  useEffect(() => {
    if (progress && !isPreviewMode && guideData.steps[currentStepIndex]) {
      debouncedSaveCurrentStep(guideData.steps[currentStepIndex].id);
    }
  }, [currentStepIndex]);

  const navigateToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex < 0 || stepIndex >= guideData.steps.length) {
        return;
      }
      const step = guideData.steps[stepIndex];
      if (!step) return;

      setCurrentStepIndex(stepIndex);
    },
    [guideData.steps],
  );

  const handleNext = useCallback(() => {
    if (currentStepIndex < guideData.steps.length - 1) {
      navigateToStep(currentStepIndex + 1);
    }
  }, [currentStepIndex, guideData.steps.length, navigateToStep]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      navigateToStep(currentStepIndex - 1);
    }
  }, [currentStepIndex, navigateToStep]);

  const markStepComplete = useCallback(
    (stepIndex: number, forceComplete: boolean = false) => {
      if (!progress || isPreviewMode || !guideData) return;

      const step = guideData.steps[stepIndex];
      if (!step) return;

      const isAlreadyCompleted = progress.completedStepIds.includes(step.id);

      // If forcing completion (e.g., quiz passed), only add if not already completed
      if (forceComplete) {
        if (!isAlreadyCompleted) {
          updateProgress({
            currentStepId: progress.currentStepId,
            completedStepIds: [...progress.completedStepIds, step.id],
            quizScores: progress.quizScores,
          });
        }
      } else {
        // Toggle behavior for manual checkbox
        const updatedStepIds = isAlreadyCompleted
          ? progress.completedStepIds.filter((id) => id !== step.id)
          : [...progress.completedStepIds, step.id];
        updateProgress({
          currentStepId: progress.currentStepId,
          completedStepIds: updatedStepIds,
          quizScores: progress.quizScores,
        });
      }
    },
    [progress, isPreviewMode, guideData, updateProgress],
  );

  // Get step translation
  const getStepTranslation = useCallback(
    (step: GuideStep): StepTranslation | null => {
      if (!step?.translations) return null;

      let translation = step.translations.find((t) => t.language === currentLanguage);
      if (!translation && step.translations.length > 0) {
        translation = step.translations[0];
      }

      return translation || null;
    },
    [currentLanguage],
  );

  // Computed values
  const currentStep = guideData.steps[currentStepIndex] || null;
  const oppositeLanguage = currentLanguage === 'en' ? 'es' : 'en';
  const hasOppositeLanguage = guideData.translations.some((t) => t.language === oppositeLanguage);
  const progressPercentage = isPreviewMode
    ? 0
    : progress
    ? Math.round((progress.completedStepIds.length / guideData.steps.length) * 100)
    : 0;
  const totalTime = guideData.estimatedTime || 0;
  const completedSteps = isPreviewMode ? 0 : progress?.completedStepIds.length || 0;
  const totalSteps = guideData.steps.length;
  const remainingSteps = totalSteps - completedSteps;
  const timePerStep = totalTime / totalSteps;
  const remainingTime = Math.ceil(remainingSteps * timePerStep);
  const timeRemaining = `${remainingTime} min`;
  // TODO: Should probably use its own language state instead of the global one.
  const handleSwitchLanguage = useCallback(() => {
    setLanguage(oppositeLanguage);
  }, [oppositeLanguage, setLanguage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (viewMode === ViewModeType.SLIDE) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            handlePrevious();
            break;
          case 'ArrowRight':
            event.preventDefault();
            handleNext();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, handleNext, handlePrevious]);

  return (
    <div className="min-h-screen bg-background">
      <GuideViewerHeader
        guideData={guideData}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onBack={() => navigate(-1)}
        onLanguageSwitch={handleSwitchLanguage}
        hasOppositeLanguage={hasOppositeLanguage}
        oppositeLanguage={oppositeLanguage}
        progressPercentage={progressPercentage}
        timeRemaining={timeRemaining}
      />

      <div className="w-full px-4 py-6">
        <div className={`flex gap-4 ${isPreviewMode ? 'h-[calc(100vh-320px)]' : 'h-[calc(100vh-200px)]'}`}>
          {/* Steps Sidebar - Left */}
          <GuideViewerSteps
            steps={guideData.steps}
            currentStepIndex={currentStepIndex}
            progress={progress}
            isOpen={isLeftSidebarOpen}
            onToggle={() => setIsLeftSidebarOpen(!isLeftSidebarOpen)}
            onStepClick={navigateToStep}
            getStepTranslation={getStepTranslation}
          />
          {/* Current Step - Main Content */}
          <div className="flex-1">
            <div className="flex flex-col h-full justify-between">
              <div className="h-full overflow-y-auto">
                {viewMode === ViewModeType.SLIDE ? (
                  <GuideSlideView
                    currentStep={currentStep}
                    currentStepIndex={currentStepIndex}
                    totalSteps={guideData.steps.length}
                    progress={isPreviewMode ? null : progress}
                    onStepComplete={markStepComplete}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    getStepTranslation={getStepTranslation}
                    guideId={guideId}
                    isSavingComplete={isSavingComplete}
                  />
                ) : (
                  <GuideScrollView
                    steps={guideData.steps}
                    progress={isPreviewMode ? null : progress}
                    onStepComplete={markStepComplete}
                    onStepInView={navigateToStep}
                    getStepTranslation={getStepTranslation}
                    guideId={guideId}
                    isSavingComplete={isSavingComplete}
                  />
                )}
              </div>
              <GuideStepAudioPlayer
                step={currentStep}
                key={currentStep.id}
                stepTranslation={getStepTranslation(currentStep)}
              />
            </div>
          </div>
          {/* Glossary Sidebar - Right */}
          <GuideViewerGlossaries
            currentStep={currentStep}
            isOpen={isRightSidebarOpen}
            onToggle={() => setIsRightSidebarOpen(!isRightSidebarOpen)}
            currentLanguage={currentLanguage}
          />
        </div>
      </div>
    </div>
  );
};

export default GuideViewer;
